import React from "react";
import { useTranslation } from "react-i18next";

import { filters } from "../data";
import type { TreeFilter } from "../models";

import LineIcon from "./LineIcon";

import styles from "../HomePage.module.css";

type TreeToolbarProps = {
	activeFilter: TreeFilter;
	isVerified: boolean;
	onCreateTree: () => void;
	onFilterChange: (filter: TreeFilter) => void;
	onSearchChange: (value: string) => void;
	searchValue: string;
};

const TreeToolbar = ({
	activeFilter,
	isVerified,
	onCreateTree,
	onFilterChange,
	onSearchChange,
	searchValue
}: TreeToolbarProps) => {
	const { t } = useTranslation("homePage");
	const onSearchInputChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>): void => {
			onSearchChange(event.target.value);
		},
		[onSearchChange]
	);
	const onFilterButtonClick = React.useCallback(
		(event: React.MouseEvent<HTMLButtonElement>): void => {
			onFilterChange(event.currentTarget.value as TreeFilter);
		},
		[onFilterChange]
	);

	return (
		<section aria-label={t("toolbar.label")} className={styles.toolbar}>
			<label className={styles.searchField} htmlFor="tree-search">
				<LineIcon name="search" />
				<span className="sr-only">{t("toolbar.searchLabel")}</span>
				<input
					id="tree-search"
					onChange={onSearchInputChange}
					placeholder={t("toolbar.searchPlaceholder")}
					type="search"
					value={searchValue}
				/>
			</label>

			<div aria-label={t("toolbar.filtersLabel")} className={styles.filterGroup}>
				{filters.map((filter: TreeFilter) => (
					<button
						key={filter}
						className={`${styles.filterChip} ${filter === activeFilter ? styles.filterChipActive : ""}`}
						onClick={onFilterButtonClick}
						type="button"
						value={filter}>
						{t(`toolbar.filters.${filter}`)}
					</button>
				))}
			</div>

			<button
				className={styles.newTreeButton}
				disabled={!isVerified}
				onClick={onCreateTree}
				title={isVerified ? undefined : t("toolbar.verifyToCreate")}
				type="button">
				<LineIcon name="plus" />
				<span>{t("toolbar.newTree")}</span>
			</button>
		</section>
	);
};

export default TreeToolbar;
